import { useEffect, useState } from 'react';
import { Box, Typography, Paper, Chip, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Global, css } from '@emotion/react';
import { ShoppingCart, Info } from 'lucide-react';
import CustomDialogSmall from '../../components/Dialog/DiaCompSmall';
import { API_URL } from '../../constants';
import axios from 'axios';
import { useAuth } from '../../provider';
import { useTranslation } from '../../language';
import { CATEGORY_NAV_KEY, SUBSYSTEM_KEY } from '../CockpitWelcome/Bloodtests/bloodtestKeys';

const FADE_UP = css`
  @keyframes hc-fade-up {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
`;

// package_id values match the addon-product skus from AddonSelection.jsx —
// reuse the same nameKey/descKey pairs so suggested add-ons render in the user's language.
const ADDON_PACKAGE_META = {
  age:              { nameKey: 'add_bio',   descKey: 'card_bio_age_desc' },
  vitamins:         { nameKey: 'add_vit',   descKey: 'card_vitamins_desc' },
  omega:            { nameKey: 'add_omega', descKey: 'card_omega_desc' },
  horm_female:      { nameKey: 'add_hormf', descKey: 'card_female_hormone_desc' },
  horm_male:        { nameKey: 'add_hormm', descKey: 'card_male_hormone_desc' },
  psa:              { nameKey: 'add_psa',   descKey: 'card_prostate_desc' },
  thyroid:          { nameKey: 'add_thyr',  descKey: 'card_thyroid_desc' },
  atherosclerosis:  { nameKey: 'add_ath',   descKey: 'card_athero_desc' },
  bones:            { nameKey: 'add_bones', descKey: 'card_bones_desc' },
  fertility:        { nameKey: 'add_ov',    descKey: 'card_ovarian_desc' },
  gutmicrobiome:    { nameKey: 'add_gut',   descKey: 'card_gut_desc' },
  ifob:             { nameKey: 'add_fob',   descKey: 'card_colon_desc' },
};

// trigger strings are "<marker_category|marker_healthsubsystem> > ..." — translate the
// category part via the same lookups BloodtestsPanel.jsx uses for those backend strings.
function translateTriggerCategory(category, t) {
  const navKey = CATEGORY_NAV_KEY[category];
  if (navKey) return t(navKey);
  const subKey = SUBSYSTEM_KEY[category];
  if (subKey) return t(subKey);
  return category;
}

function useTokens() {
  const theme = useTheme();
  const p     = theme.palette;
  const dark  = p.mode === 'dark';

  const riskAlphaBg     = dark ? 0.15 : 0.08;
  const riskAlphaBorder = dark ? 0.40 : 0.30;
  const red             = p.traffic.red;
  const green           = p.traffic.green;

  const T = {
    dark,
    cardBg:      p.background.paper,
    cardBorder:  dark ? p.greydark.C500  : p.greydark.C50,
    cardShadow:  dark ? '0 2px 20px rgba(0,0,0,0.5)' : '0 2px 14px rgba(0,0,0,0.07)',
    sectionLine: dark ? p.greydark.C600  : p.grey.C200,
    heading:     dark ? p.grey.C50       : p.greydark.C900,
    body:        dark ? p.grey.C600      : p.greydark.C500,
    bodyStrong:  dark ? p.grey.C300      : p.greydark.C700,
    overline:    dark ? p.grey.C800      : p.grey.C700,
    chipBg:      dark ? p.greydark.C700  : p.grey.C100,
    chipText:    dark ? p.grey.C700      : p.greydark.C500,
  };

  const PRIORITY_CONFIG = {
    high: { dot: red,   bg: alpha(red,   riskAlphaBg), border: alpha(red,   riskAlphaBorder) },
    low:  { dot: green, bg: alpha(green, riskAlphaBg), border: alpha(green, riskAlphaBorder) },
  };

  return { T, PRIORITY_CONFIG };
}

function SectionLabel({ index, title, info }) {
  const { T } = useTokens();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center',  mb: 4 }}>
        
        <Box sx={{ flexShrink: 0 }}>
          
          <Typography sx={{
            fontSize: '1.05rem', fontWeight: 700, color: T.heading,
            mt: 0.15, lineHeight: 1.2,
          }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, height: '1px', bgcolor: T.sectionLine }} />
        {info && (
          <IconButton
            onClick={() => setOpen(true)}
            sx={{ color: T.overline, '&:hover': { color: T.body }, p: 0.5, flexShrink: 0 }}
          >
            <Info size={18} strokeWidth={2.5} />
          </IconButton>
        )}
      </Box>
      {info && (
        <CustomDialogSmall
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          content={
           
            typeof info === 'string' ? (
               <Box sx={{mt:4}}>
              <Typography sx={{ fontSize: '0.85rem', color: T.body, lineHeight: 1.75 }}>
                {info}
              </Typography>
               </Box>
            ) : info
         
          }
        />
      )}
    </>
  );
}

function AddonCard({ addon, animIndex = 0 }) {
  const { T, PRIORITY_CONFIG } = useTokens();
  const theme = useTheme();
  const { t } = useTranslation();
  const isRec = addon.urgency === 'Recommended';
  const cfg   = isRec ? PRIORITY_CONFIG.high : PRIORITY_CONFIG.low;
  const meta  = ADDON_PACKAGE_META[addon.package_id];
  const name        = meta ? t(meta.nameKey) : addon.name;
  const description = meta ? t(meta.descKey) : addon.description;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
        boxShadow: T.dark
          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.22s, transform 0.22s, border-color 0.22s',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
        animation: 'hc-fade-up 0.5s ease both',
        animationDelay: `${animIndex * 65}ms`,
      }}
    >
      <Box sx={{
        px: 2.25, pt: 2, pb: 1.75,
        bgcolor: T.dark ? alpha('#fff', 0.03) : alpha('#fff', 0.35),
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center',
          px: 1.25, py: 0.3, borderRadius: '6px',
          bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
        }}>
          <Box component="span" sx={{
            fontSize: '0.5rem', fontWeight: 400, color: cfg.dot,
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            {isRec ? t('hc_urgency_recommended') : t('hc_urgency_optional')}
          </Box>
        </Box>
        <ShoppingCart size={14} color={theme.palette.tertiary.C500} />
      </Box>

      <Box sx={{ px: 2.25, pt: 2, pb: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.heading, lineHeight: 1.35 }}>
          {name}
        </Typography>

        {addon.triggered_by?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.55rem', color: T.overline }}>
              {t('hc_triggered_by')}
            </Typography>
            {addon.triggered_by.slice(0, 3).map((trigger) => (
              <Chip
                key={trigger}
                label={translateTriggerCategory(trigger.split('>')[0]?.trim(), t)}
                size="small"
                sx={{ height: '18px', fontSize: '0.55rem', fontWeight: 700, bgcolor: T.chipBg, color: T.chipText, borderRadius: '4px' }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ fontSize: '0.65rem', color: T.body, lineHeight: 1.7 }}>
          {description}
        </Box>
      </Box>
    </Paper>
  );
}

export default function AddonSuggestionsSection() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const userId = state.user.id;

  const [addons, setAddons] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const { data: dates } = await axios.get(`${API_URL}/api/user/allmeasurements/core_membership/`);
        if (cancelled) return;
        const sorted = [...new Set(dates.map((d) => d.date))].sort((a, b) => new Date(b) - new Date(a));
        if (!sorted.length) { if (!cancelled) setLoading(false); return; }

        const { data: report } = await axios.get(`${API_URL}/api/reports/${userId}/${sorted[0]}/`);
        if (!cancelled) setAddons(report.addon_suggestions ?? []);
      } catch {
        // silently hide the section on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading || !addons?.length) return null;

  return (
    <>
      <Global styles={FADE_UP} />
      <Box sx={{ maxWidth: '1100px', mx: 'auto', px: { xs: 2, sm: 4, md: 6 }, width: '100%', mt: 2, mb: 16 }}>
        <SectionLabel
          index="04"
          title={t('hc_section_04_title')}
          info={t('hc_section_04_info')}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          {addons.map((addon, i) => (
            <AddonCard key={addon.package_id} addon={addon} animIndex={i} />
          ))}
        </Box>
      </Box>
    </>
  );
}
